/**
 * WB4.5 — Cross-family action id semantic alias SSOT.
 *
 * Sellable golden slice uses sellable-specific ids (`start_order`, `edit_description`);
 * domain families use domain top-5 ids (`start_order_workflow`, `edit`).
 * This module documents intentional pairs and powers static parity gates.
 */

import { ACTION_SURFACE_PARITY_MATRIX } from './action-surface-parity-matrix';
import { AI_SUGGEST_PILOT_ACTION_IDS_BY_DOMAIN } from './ai-suggest-pilot-registry';
import {
  DOMAIN_ACTION_PARITY_FAMILY_ANCHORS,
  resolveDomainTop5ActionIds,
  type DomainActionFamily,
} from './domain-action-rule-table';
import {
  SELLABLE_GOLDEN_ACTION_RULE_TABLE,
  SELLABLE_GOLDEN_HANDLER_REFS,
} from './sellable-action-rule-table';
import type { ActionRuleEntry } from './action-types';

export interface DomainActionSemanticAlias {
  /** Sellable golden slice id */
  sellableId: string;
  /** Domain family top-5 id */
  domainId: string;
  /** Shared handler ref when both lanes must converge */
  sharedHandlerRef?: string;
  /** When true, handler refs may diverge by design (documented exception) */
  allowHandlerDivergence?: boolean;
  note?: string;
}

/** Documented sellable ↔ domain semantic alias pairs. */
export const DOMAIN_ACTION_SEMANTIC_ALIASES: readonly DomainActionSemanticAlias[] = [
  {
    sellableId: 'start_order',
    domainId: 'start_order_workflow',
    sharedHandlerRef: SELLABLE_GOLDEN_HANDLER_REFS.startOrder,
    note: 'Order intake workflow — sellable_item vs order/customer families',
  },
  {
    sellableId: 'edit_description',
    domainId: 'edit',
    allowHandlerDivergence: true,
    note: 'Sellable AI lane uses workbench:edit-description; domain families use crud:edit',
  },
  {
    sellableId: 'start_inventory_workflow',
    domainId: 'start_inventory_workflow',
    sharedHandlerRef: SELLABLE_GOLDEN_HANDLER_REFS.startInventoryWorkflow,
  },
  {
    sellableId: 'open_detail',
    domainId: 'open_detail',
    sharedHandlerRef: SELLABLE_GOLDEN_HANDLER_REFS.openDetail,
  },
  {
    sellableId: 'view_audit',
    domainId: 'view_audit',
    sharedHandlerRef: SELLABLE_GOLDEN_HANDLER_REFS.viewAudit,
  },
] as const;

/** Domain families enrolled in sellable ↔ domain alias parity (auto-synced with rule table families). */
export const DOMAIN_ACTION_ID_PARITY_PILOT_FAMILIES: readonly {
  family: DomainActionFamily;
  domain: string;
}[] = (Object.keys(DOMAIN_ACTION_PARITY_FAMILY_ANCHORS) as DomainActionFamily[]).map((family) => ({
  family,
  domain: DOMAIN_ACTION_PARITY_FAMILY_ANCHORS[family],
}));

export interface DomainActionIdParityViolation {
  reason: string;
}

function collectHandlerRefByActionId(table: readonly ActionRuleEntry[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of table) {
    for (const action of entry.actions) {
      if (!map.has(action.id)) {
        map.set(action.id, action.handlerRef);
      }
    }
  }
  return map;
}

export function collectDomainActionIdParityViolations(): DomainActionIdParityViolation[] {
  const violations: DomainActionIdParityViolation[] = [];
  const sellableHandlers = collectHandlerRefByActionId(SELLABLE_GOLDEN_ACTION_RULE_TABLE);

  for (const alias of DOMAIN_ACTION_SEMANTIC_ALIASES) {
    if (!ACTION_SURFACE_PARITY_MATRIX[alias.sellableId]) {
      violations.push({
        reason: `alias sellable id ${alias.sellableId} missing from ACTION_SURFACE_PARITY_MATRIX`,
      });
    }
    if (!ACTION_SURFACE_PARITY_MATRIX[alias.domainId]) {
      violations.push({
        reason: `alias domain id ${alias.domainId} missing from ACTION_SURFACE_PARITY_MATRIX`,
      });
    }

    const sellableHandler = sellableHandlers.get(alias.sellableId);

    if (alias.sharedHandlerRef) {
      if (sellableHandler !== alias.sharedHandlerRef) {
        violations.push({
          reason: `sellable ${alias.sellableId} handler ${sellableHandler ?? 'missing'} !== ${alias.sharedHandlerRef}`,
        });
      }
    } else if (!alias.allowHandlerDivergence && sellableHandler) {
      violations.push({
        reason: `alias ${alias.sellableId}↔${alias.domainId} missing sharedHandlerRef documentation`,
      });
    }
  }

  for (const { family, domain } of DOMAIN_ACTION_ID_PARITY_PILOT_FAMILIES) {
    const topIds = resolveDomainTop5ActionIds(domain);
    if (!topIds) {
      violations.push({ reason: `${family}: missing top-5 ids for ${domain}` });
      continue;
    }

    for (const sellableOnlyId of ['start_order', 'edit_description'] as const) {
      if (topIds.includes(sellableOnlyId)) {
        violations.push({
          reason: `${family}/${domain}: top-5 must use domain id, not sellable id ${sellableOnlyId}`,
        });
      }
    }

    const alias = DOMAIN_ACTION_SEMANTIC_ALIASES.find(
      (entry) => entry.sellableId === 'start_order'
    );
    if (family === 'order' && alias && !topIds.includes(alias.domainId)) {
      violations.push({
        reason: `order/${domain}: top-5 must include ${alias.domainId}`,
      });
    }
  }

  for (const [domain, pilotIds] of Object.entries(AI_SUGGEST_PILOT_ACTION_IDS_BY_DOMAIN)) {
    for (const actionId of pilotIds) {
      if (actionId === 'start_order' || actionId === 'edit_description') {
        if (domain !== 'sellable_item') {
          violations.push({
            reason: `AI pilot ${domain} uses sellable-only id ${actionId}; use domain alias id`,
          });
        }
      }
    }
  }

  if (SELLABLE_GOLDEN_HANDLER_REFS.startOrder !== 'flow:start:order_intake') {
    violations.push({
      reason: 'SELLABLE_GOLDEN_HANDLER_REFS.startOrder diverged from domain order workflow ref',
    });
  }

  return violations;
}
